import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Chip,
    Alert,
    Tooltip,
    Fade,
    Dialog,
    DialogContent,
    useMediaQuery,
    useTheme,
    LinearProgress,
    Divider,
    Avatar
} from '@mui/material';
import {
    Fingerprint as FingerprintIcon,
    Login as LoginIcon,
    Logout as LogoutIcon,
    VideocamOff as VideocamOffIcon,
    Refresh as RefreshIcon,
    LocationOn as LocationOnIcon,
    LocationOff as LocationOffIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    Cameraswitch as CameraswitchIcon,
    Close as CloseIcon,
    CenterFocusStrong as CenterFocusStrongIcon,
    CameraAlt as CameraAltIcon,
    GpsFixed as GpsFixedIcon,
    Sensors as SensorsIcon,
    Send as SendIcon,
    Replay as ReplayIcon,
    Place as PlaceIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

// Audio feedback assets
import errorSound from '../Assets/error.mp3';
import successSound from '../Assets/success.mp3';

// Base API configuration from environment or fallback
const DEFAULT_API_BASE = process.env.REACT_APP_WEBSITE_URL || 'http://localhost:8000';

const Biometric = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Redux global / auth state
    const authUser = useSelector((state) => state?.auth?.user);
    const globalVars = useSelector((state) => state?.globalVariables);

    // Employee & Company parameters (used only in API payload)
    const employeeCode = authUser?.employee_code || authUser?.emp_code || authUser?.code || "sepha100008";
    const companyKey = "WyC7GcHt";

    // --- Audio Helper ---
    const playAudio = (soundFile) => {
        try {
            const audio = new Audio(soundFile);
            audio.play().catch((err) => {
                console.warn("Audio autoplay blocked or failed:", err);
            });
        } catch (e) {
            console.warn("Audio error:", e);
        }
    };

    // --- State: Live Clock ---
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // --- State: Modal, Camera & Verification Flow ---
    const [modalOpen, setModalOpen] = useState(false);
    const [punchType, setPunchType] = useState(null); // 'IN' | 'OUT'
    const [scanStage, setScanStage] = useState('camera'); // 'camera' | 'review' | 'submitting' | 'error'
    const [scanProgress, setScanProgress] = useState(0);
    const [cameraError, setCameraError] = useState('');
    const [facingMode, setFacingMode] = useState('user');

    // Verified Biometric & Location Data (No hardcoded fallback coords)
    const [capturedSnapshot, setCapturedSnapshot] = useState(null); // { file, blob, previewUrl }
    const [locationData, setLocationData] = useState(null); // { latitude, longitude, place, location, accuracy }
    const [locationError, setLocationError] = useState(''); // Error message if location is disabled/denied
    const [isLocating, setIsLocating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // References
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const autoScanTimerRef = useRef(null);

    // --- State: Last Punch Result ---
    const [lastPunch, setLastPunch] = useState(null);

    // Helper to stop camera media tracks
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (autoScanTimerRef.current) clearTimeout(autoScanTimerRef.current);
    }, []);

    // Reverse Geocoding Helper: Converts real Latitude & Longitude to Real-time Place/Address
    const reverseGeocode = async (lat, lon) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=jsonv2&addressdetails=1&zoom=18`
            );
            if (!response.ok) throw new Error('Geocoding request failed');
            const data = await response.json();
            const a = data?.address || {};

            const nearest = a.amenity || a.building || a.shop || a.office || a.road || a.neighbourhood;
            const locality = a.suburb || a.neighbourhood || a.village || a.town;
            const city = a.city || a.town || a.county || a.state_district;
            const state = a.state;
            const pincode = a.postcode;

            const parts = [];
            if (nearest) parts.push(nearest);
            if (locality && locality !== nearest) parts.push(locality);
            if (city && city !== locality) parts.push(city);
            if (state) parts.push(state);
            if (pincode) parts.push(pincode);

            return parts.length ? parts.join(', ') : (data?.display_name || `${lat}, ${lon}`);
        } catch (err) {
            console.warn("Reverse geocode fallback used:", err);
            return `${lat}, ${lon}`;
        }
    };

    // Real-time Geolocation & Place Fetcher (Strict: No hardcoded fallback)
    const fetchCurrentLocation = useCallback(() => {
        return new Promise((resolve) => {
            setIsLocating(true);
            setLocationError('');

            if (!('geolocation' in navigator)) {
                setIsLocating(false);
                setLocationData(null);
                const errText = 'Geolocation is not supported by your browser or connection is not secure (HTTPS required).';
                setLocationError(errText);
                resolve(null);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude.toFixed(6).toString();
                    const lon = position.coords.longitude.toFixed(6).toString();
                    const accuracy = Math.round(position.coords.accuracy || 10);

                    // Fetch human-readable real-time address
                    let placeAddress = `${lat}, ${lon}`;
                    try {
                        placeAddress = await reverseGeocode(lat, lon);
                    } catch (e) {
                        console.warn(e);
                    }

                    const coords = {
                        latitude: lat,
                        longitude: lon,
                        place: placeAddress,
                        location: placeAddress,
                        accuracy: accuracy
                    };
                    setLocationData(coords);
                    setLocationError('');
                    setIsLocating(false);
                    resolve(coords);
                },
                (error) => {
                    console.warn("Geolocation access failed:", error);
                    setIsLocating(false);
                    setLocationData(null);

                    let errMsg = 'Location permission is required to punch in or out. Please enable location services in your device/browser settings.';
                    if (error.code === error.PERMISSION_DENIED) {
                        errMsg = 'Location permission was denied. Please allow location access in your browser settings to proceed.';
                    } else if (error.code === error.POSITION_UNAVAILABLE) {
                        errMsg = 'Location information is unavailable. Please check GPS/network connectivity and retry.';
                    } else if (error.code === error.TIMEOUT) {
                        errMsg = 'Location request timed out. Please click "Retry Location" with active GPS.';
                    }

                    setLocationError(errMsg);
                    resolve(null);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 12000,
                    maximumAge: 0
                }
            );
        });
    }, []);

    // Initialize Webcam
    const startCamera = useCallback(async () => {
        setScanStage('camera');
        setCameraError('');
        setScanProgress(0);

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCameraError('Webcam access is not supported on this browser or connection is not secure (HTTPS required).');
            setScanStage('error');
            return;
        }

        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }

            const constraints = {
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = mediaStream;

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play().catch(console.error);
                };
            }

            // Start simulated face scanning progress
            let current = 0;
            progressIntervalRef.current = setInterval(() => {
                current += 5;
                if (current >= 100) {
                    current = 100;
                    clearInterval(progressIntervalRef.current);
                }
                setScanProgress(current);
            }, 90);

            // Auto-capture after scan progress finishes
            autoScanTimerRef.current = setTimeout(() => {
                handleCaptureFrame();
            }, 2100);

        } catch (err) {
            console.error("Camera access error:", err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setCameraError('Camera permission denied. Please allow camera permissions in browser settings.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setCameraError('No camera device found. Please connect a webcam.');
            } else {
                setCameraError(`Camera Error: ${err.message || 'Unable to access camera'}`);
            }
            setScanStage('error');
        }
    }, [facingMode]);

    // Handle Open Pop-up when user clicks Punch In or Punch Out
    const handleOpenPunchModal = (type) => {
        setPunchType(type);
        setCapturedSnapshot(null);
        setLocationData(null);
        setLocationError('');
        setModalOpen(true);
    };

    // Trigger Camera on modal open
    useEffect(() => {
        if (modalOpen) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => {
            stopCamera();
        };
    }, [modalOpen, facingMode]);

    // Toggle Camera (front/back on mobile)
    const toggleCamera = () => {
        if (autoScanTimerRef.current) clearTimeout(autoScanTimerRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
    };

    // Capture Image Frame from live Video
    const captureFrameBlob = () => {
        if (!videoRef.current || !canvasRef.current) return null;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const width = video.videoWidth || 640;
        const height = video.videoHeight || 480;

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (facingMode === 'user') {
            ctx.translate(width, 0);
            ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0, width, height);

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        resolve(null);
                        return;
                    }
                    const file = new File([blob], `biometric_punch_${Date.now()}.jpg`, {
                        type: 'image/jpeg'
                    });
                    const previewUrl = canvas.toDataURL('image/jpeg', 0.92);
                    resolve({ file, blob, previewUrl });
                },
                'image/jpeg',
                0.92
            );
        });
    };

    // Capture Frame -> Stop Camera -> Fetch GPS & Place -> Stay on Review screen inside Pop-up
    const handleCaptureFrame = async () => {
        if (autoScanTimerRef.current) clearTimeout(autoScanTimerRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

        try {
            // 1. Capture snapshot
            const snapshot = await captureFrameBlob();
            if (!snapshot || !snapshot.file) {
                throw new Error('Failed to capture frame. Please try again.');
            }

            // 2. Shut off camera immediately
            stopCamera();

            setCapturedSnapshot(snapshot);
            setScanStage('review');

            // 3. Fetch real-time geolocation and place name (strict location requirement)
            await fetchCurrentLocation();

        } catch (err) {
            console.error("Capture error:", err);
            toast.error(err.message || 'Capture failed');
            startCamera();
        }
    };

    // Retake Photo
    const handleRetake = () => {
        setCapturedSnapshot(null);
        startCamera();
    };

    // Close Modal handler
    const handleCloseModal = () => {
        if (isSubmitting) return;
        stopCamera();
        setModalOpen(false);
    };

    // STRICT CHECK: Both image and REAL location must be available with no error
    const isReadyToPunch = Boolean(
        capturedSnapshot?.file &&
        locationData?.latitude &&
        locationData?.longitude &&
        !locationError &&
        !isLocating
    );

    // Submit Final Punch Request to API with Status & Audio Validation
    const handleSubmitPunch = async () => {
        if (!isReadyToPunch) {
            if (!locationData || locationError) {
                toast.error('Location is required. Please allow location access to punch in/out.');
            } else {
                toast.error('Please complete face scan and location verification first.');
            }
            return;
        }

        setIsSubmitting(true);

        try {
            const placeAddress = locationData.place || locationData.location || `${locationData.latitude}, ${locationData.longitude}`;

            const formData = new FormData();
            formData.append("image", capturedSnapshot.file);
            formData.append("employee_code", employeeCode);
            formData.append("company_key", companyKey);
            formData.append("type", punchType); // "IN" or "OUT"
            formData.append("latitude", locationData.latitude);
            formData.append("longitude", locationData.longitude);
            // Send real-time place/location in payload
            formData.append("location", placeAddress);
            formData.append("place", placeAddress);

            const response = await axios.post(
                `${DEFAULT_API_BASE}/api/biometric-authentication`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // Close review dialog
            setModalOpen(false);

            const responseData = response.data;
            const punchTimeStr = formatTime(new Date());

            // Check if status is boolean true, string "true", 1, or "success"
            const isStatusSuccess =
                responseData?.status === true ||
                responseData?.status === 1 ||
                responseData?.status === "true" ||
                responseData?.status === "success";

            if (isStatusSuccess) {
                // Play Success Audio
                playAudio(successSound);

                setLastPunch({
                    type: punchType,
                    time: punchTimeStr,
                    status: 'success',
                    message: responseData?.message || `Punch ${punchType} Successfully Verified & Recorded!`,
                    location: placeAddress,
                    coordinates: `${locationData.latitude}, ${locationData.longitude}`,
                    previewUrl: capturedSnapshot.previewUrl
                });

                // Success SweetAlert2 Dialog
                Swal.fire({
                    title: `<span style="color: ${punchType === 'IN' ? '#059669' : '#dc2626'}; font-weight: 700; font-size: 22px;">Punch ${punchType} Successful!</span>`,
                    html: `
                        <div style="display: flex; flex-direction: column; align-items: center; gap: 12px; color: #334155; font-size: 14px;">
                            <img src="${capturedSnapshot.previewUrl}" alt="Scan Snapshot" style="width: 100px; height: 100px; border-radius: 50%; object-fit: cover; border: 3px solid ${punchType === 'IN' ? '#10b981' : '#ef4444'}; box-shadow: 0 4px 12px rgba(0,0,0,0.1); margin-top: 4px;" />
                            <div style="text-align: left; width: 100%; background: #f8fafc; padding: 14px; border-radius: 10px; border: 1px solid #e2e8f0;">
                                <p style="margin: 4px 0;"><strong>Timestamp:</strong> ${punchTimeStr}</p>
                                <p style="margin: 4px 0;"><strong>Place:</strong> <span style="color: #059669; font-weight: 600;">${placeAddress}</span></p>
                                <p style="margin: 4px 0; font-size: 12px; color: #64748b;"><strong>GPS:</strong> ${locationData.latitude}, ${locationData.longitude}</p>
                                <p style="margin: 4px 0; color: #059669; font-size: 13px; font-weight: 600;"><strong>Status:</strong> ${responseData?.message || 'Biometric Verification Passed'}</p>
                            </div>
                        </div>
                    `,
                    icon: 'success',
                    background: '#ffffff',
                    color: '#0f172a',
                    confirmButtonColor: punchType === 'IN' ? '#10b981' : '#ef4444',
                    confirmButtonText: 'Done',
                    timer: 4500,
                    timerProgressBar: true
                });

                toast.success(responseData?.message || `Punch ${punchType} recorded successfully!`);

            } else {
                // Backend returned status: false (e.g., "Please punch IN first.", "Already punched IN.")
                playAudio(errorSound);

                const errorMsg = responseData?.message || responseData?.error || `Punch ${punchType} failed.`;

                setLastPunch({
                    type: punchType,
                    time: punchTimeStr,
                    status: 'error',
                    message: errorMsg
                });

                // Error SweetAlert2 Dialog
                Swal.fire({
                    title: '<span style="color: #dc2626; font-weight: 700; font-size: 20px;">Punch Rejected</span>',
                    html: `
                        <div style="color: #334155; font-size: 14px; margin-top: 8px;">
                            <p style="font-weight: 600; font-size: 15px; color: #dc2626; margin-bottom: 8px;">${errorMsg}</p>
                            <p style="color: #64748b; font-size: 13px;">Please verify attendance sequence or try again.</p>
                        </div>
                    `,
                    icon: 'error',
                    background: '#ffffff',
                    color: '#0f172a',
                    confirmButtonColor: '#ef4444',
                    confirmButtonText: 'Understood'
                });

                toast.error(errorMsg);
            }

        } catch (err) {
            console.error("Biometric punch API error:", err);
            // Play Error Audio
            playAudio(errorSound);

            const errorMsg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Biometric verification failed. Please try again.';

            setLastPunch({
                type: punchType,
                time: formatTime(new Date()),
                status: 'error',
                message: errorMsg
            });

            // Error SweetAlert2 Dialog
            Swal.fire({
                title: '<span style="color: #dc2626; font-weight: 700; font-size: 20px;">Verification Failed</span>',
                html: `
                    <div style="color: #334155; font-size: 14px; margin-top: 8px;">
                        <p style="font-weight: 600; font-size: 15px; color: #dc2626; margin-bottom: 8px;">${errorMsg}</p>
                        <p style="color: #64748b; font-size: 13px;">Please try again or contact administrator.</p>
                    </div>
                `,
                icon: 'error',
                background: '#ffffff',
                color: '#0f172a',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Try Again'
            });

            toast.error(`Punch ${punchType} failed: ${errorMsg}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                bgcolor: '#f1f5f9',
                backgroundImage: `
                    radial-gradient(circle at 50% 0%, rgba(2, 132, 199, 0.08) 0%, transparent 60%),
                    radial-gradient(circle at 100% 100%, rgba(16, 185, 129, 0.05) 0%, transparent 40%),
                    radial-gradient(circle at 0% 100%, rgba(239, 68, 68, 0.05) 0%, transparent 40%)
                `,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 3, md: 4 },
                boxSizing: 'border-box',
                fontFamily: '"Inter", "Roboto", -apple-system, sans-serif',
                color: '#0f172a',
                position: 'relative'
            }}
        >
            {/* --- GO TO LOGIN BUTTON (Top Left Corner) --- */}
            <Button
                onClick={() => navigate('/login')}
                startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                    position: { xs: 'fixed', sm: 'absolute' },
                    top: { xs: 14, sm: 22, md: 28 },
                    left: { xs: 14, sm: 22, md: 28 },
                    zIndex: 100,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    border: '1px solid #cbd5e1',
                    borderRadius: 2.5,
                    px: { xs: 1.6, sm: 2.2 },
                    py: { xs: 0.7, sm: 0.9 },
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                        bgcolor: '#f8fafc',
                        borderColor: '#0284c7',
                        color: '#0284c7',
                        boxShadow: '0 6px 16px rgba(2, 132, 199, 0.18)',
                        transform: 'translateY(-2px)'
                    },
                    '&:active': {
                        transform: 'translateY(0)'
                    }
                }}
            >
                Go to Login
            </Button>

            {/* Hidden Canvas for Video Frame Capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Main Central Terminal Container - Light Theme */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 960,
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: { xs: 3, sm: 4, md: 5 },
                    boxShadow: '0 20px 45px -10px rgba(15, 23, 42, 0.08), 0 0 25px rgba(2, 132, 199, 0.04)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    mt: { xs: 6, sm: 0 }
                }}
            >
                {/* --- Top Header Bar --- */}
                <Box
                    sx={{
                        px: { xs: 2.5, sm: 4 },
                        py: 2.4,
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        bgcolor: '#fafafa'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                bgcolor: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#0284c7',
                                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.12)'
                            }}
                        >
                            <FingerprintIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.05rem', sm: '1.25rem' },
                                    letterSpacing: '-0.01em',
                                    color: '#0f172a',
                                    lineHeight: 1.2
                                }}
                            >
                                Biometric Attendance Terminal
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.78rem',
                                    fontWeight: 500
                                }}
                            >
                                Secure Face Recognition & Real-time Location Verification
                            </Typography>
                        </Box>
                    </Box>

                    {/* Status Chip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Chip
                            icon={
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#10b981',
                                        boxShadow: '0 0 6px #10b981',
                                        ml: 1
                                    }}
                                />
                            }
                            label="TERMINAL ACTIVE"
                            size="small"
                            sx={{
                                bgcolor: '#ecfdf5',
                                color: '#065f46',
                                border: '1px solid #a7f3d0',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                letterSpacing: '0.04em'
                            }}
                        />
                    </Box>
                </Box>

                {/* --- Body Section --- */}
                <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
                    {/* Top Interactive Area: Clock + IN/OUT Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'center',
                            justifyContent: 'space-around',
                            gap: { xs: 4, md: 5 },
                            p: { xs: 3, sm: 4.5 },
                            borderRadius: 4,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)'
                        }}
                    >
                        {/* 1. Left: Circular Clock */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 180, sm: 210, md: 220 },
                                    height: { xs: 180, sm: 210, md: 220 },
                                    borderRadius: '50%',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#ffffff',
                                    border: '4px solid #0284c7',
                                    boxShadow: '0 10px 30px rgba(2, 132, 199, 0.15), inset 0 0 15px rgba(2, 132, 199, 0.05)',
                                    transition: 'box-shadow 0.3s ease',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: -10,
                                        borderRadius: '50%',
                                        border: '1px dashed #7dd3fc',
                                        pointerEvents: 'none'
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#0284c7',
                                        fontSize: { xs: '0.75rem', sm: '0.82rem' },
                                        fontWeight: 700,
                                        letterSpacing: '0.16em',
                                        textTransform: 'uppercase',
                                        mb: 0.5
                                    }}
                                >
                                    CURRENT TIME
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#0f172a',
                                        fontSize: { xs: '1.75rem', sm: '2.1rem', md: '2.25rem' },
                                        fontWeight: 800,
                                        letterSpacing: '0.02em',
                                        fontFamily: 'monospace, "Inter", sans-serif',
                                        lineHeight: 1
                                    }}
                                >
                                    {formatTime(currentTime)}
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#64748b',
                                        fontSize: { xs: '0.72rem', sm: '0.8rem' },
                                        fontWeight: 500,
                                        mt: 0.8
                                    }}
                                >
                                    {formatDate(currentTime)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Divider for desktop */}
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                width: '1px',
                                height: 180,
                                bgcolor: '#e2e8f0'
                            }}
                        />

                        {/* 2. Right: Action Buttons (IN & OUT) */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: { xs: 3.5, sm: 5, md: 6 },
                                flexWrap: 'wrap'
                            }}
                        >
                            {/* --- IN BUTTON --- */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                <Button
                                    onClick={() => handleOpenPunchModal('IN')}
                                    sx={{
                                        width: { xs: 110, sm: 125, md: 135 },
                                        height: { xs: 110, sm: 125, md: 135 },
                                        borderRadius: '50%',
                                        p: 0,
                                        minWidth: 'unset',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.35), 0 0 15px rgba(16, 185, 129, 0.15)',
                                        border: '3px solid #ffffff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
                                            transform: 'scale(1.08) translateY(-4px)',
                                            boxShadow: '0 16px 35px rgba(16, 185, 129, 0.5), 0 0 25px rgba(16, 185, 129, 0.3)'
                                        },
                                        '&:active': {
                                            transform: 'scale(0.96)'
                                        }
                                    }}
                                >
                                    <LoginIcon sx={{ fontSize: { xs: 38, sm: 44 }, color: '#ffffff', mb: 0.3 }} />
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: '#ffffff',
                                            lineHeight: 1
                                        }}
                                    >
                                        IN
                                    </Typography>
                                </Button>
                                <Typography
                                    sx={{
                                        mt: 1.5,
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: '#059669',
                                        letterSpacing: '0.06em'
                                    }}
                                >
                                    PUNCH IN
                                </Typography>
                            </Box>

                            {/* --- OUT BUTTON --- */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                <Button
                                    onClick={() => handleOpenPunchModal('OUT')}
                                    sx={{
                                        width: { xs: 110, sm: 125, md: 135 },
                                        height: { xs: 110, sm: 125, md: 135 },
                                        borderRadius: '50%',
                                        p: 0,
                                        minWidth: 'unset',
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        boxShadow: '0 10px 25px rgba(239, 68, 68, 0.35), 0 0 15px rgba(239, 68, 68, 0.15)',
                                        border: '3px solid #ffffff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #f87171 0%, #b91c1c 100%)',
                                            transform: 'scale(1.08) translateY(-4px)',
                                            boxShadow: '0 16px 35px rgba(239, 68, 68, 0.5), 0 0 25px rgba(239, 68, 68, 0.3)'
                                        },
                                        '&:active': {
                                            transform: 'scale(0.96)'
                                        }
                                    }}
                                >
                                    <LogoutIcon sx={{ fontSize: { xs: 38, sm: 44 }, color: '#ffffff', mb: 0.3 }} />
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: '#ffffff',
                                            lineHeight: 1
                                        }}
                                    >
                                        OUT
                                    </Typography>
                                </Button>
                                <Typography
                                    sx={{
                                        mt: 1.5,
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: '#dc2626',
                                        letterSpacing: '0.06em'
                                    }}
                                >
                                    PUNCH OUT
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Metadata Footer */}
                    <Box
                        sx={{
                            mt: 3.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            px: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <GpsFixedIcon sx={{ fontSize: 18, color: '#0284c7' }} />
                            <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                                Location Mode: <strong style={{ color: '#0284c7' }}>Mandatory Real-time GPS Location</strong>
                            </Typography>
                        </Box>
                    </Box>

                    {/* --- Last Punch Status Alert Banner --- */}
                    {lastPunch && (
                        <Fade in={Boolean(lastPunch)}>
                            <Alert
                                severity={lastPunch.status === 'success' ? 'success' : 'error'}
                                icon={lastPunch.status === 'success' ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />}
                                sx={{
                                    mt: 3.5,
                                    bgcolor: lastPunch.status === 'success' ? '#f0fdf4' : '#fef2f2',
                                    color: '#0f172a',
                                    border: `1px solid ${lastPunch.status === 'success' ? '#bbf7d0' : '#fecaca'}`,
                                    borderRadius: 3,
                                    '& .MuiAlert-icon': {
                                        color: lastPunch.status === 'success' ? '#16a34a' : '#dc2626'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                    Punch {lastPunch.type} {lastPunch.status === 'success' ? 'Successful' : 'Rejected'} at {lastPunch.time}
                                </Typography>
                                <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
                                    {lastPunch.message}
                                </Typography>
                                {lastPunch.location && (
                                    <Typography sx={{ fontSize: '0.78rem', color: '#0369a1', fontWeight: 600, mt: 0.5 }}>
                                        📍 Place: {lastPunch.location}
                                    </Typography>
                                )}
                            </Alert>
                        </Fade>
                    )}
                </Box>
            </Box>

            {/* ============================================================== */}
            {/* --- BIOMETRIC FACE SCAN POP-UP DIALOG (LIGHT THEME) --- */}
            {/* ============================================================== */}
            <Dialog
                open={modalOpen}
                onClose={isSubmitting ? undefined : handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: { xs: 3, sm: 4 },
                        boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.25)',
                        overflow: 'hidden',
                        color: '#0f172a'
                    }
                }}
            >
                {/* Pop-up Header */}
                <Box
                    sx={{
                        p: 2.2,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #f1f5f9',
                        bgcolor: '#f8fafc'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CenterFocusStrongIcon sx={{ color: punchType === 'IN' ? '#059669' : '#dc2626', fontSize: 28 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>
                                Facial Biometric Verification — Punch {punchType}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {scanStage === 'camera' ? 'Keep face centered in frame during scanning' : 'Review captured photo and real-time place before confirming'}
                            </Typography>
                        </Box>
                    </Box>

                    {!isSubmitting && (
                        <IconButton
                            onClick={handleCloseModal}
                            size="small"
                            sx={{
                                color: '#64748b',
                                '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {/* Pop-up Content */}
                <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* VIEW 1: LIVE WEBCAM & ACTIVE SCANNING HUD */}
                    {scanStage === 'camera' && (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 300, sm: 360 },
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    bgcolor: '#090d16',
                                    border: '2px solid #0284c7',
                                    boxShadow: '0 12px 30px rgba(2, 132, 199, 0.2), inset 0 0 35px rgba(0, 0, 0, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {/* Live Video Stream */}
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                                    }}
                                />

                                {/* High-tech Laser Scan Beam Animation */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: punchType === 'IN'
                                            ? 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)'
                                            : 'linear-gradient(90deg, transparent 0%, #ef4444 50%, transparent 100%)',
                                        boxShadow: punchType === 'IN'
                                            ? '0 0 15px #10b981, 0 0 30px #10b981'
                                            : '0 0 15px #ef4444, 0 0 30px #ef4444',
                                        pointerEvents: 'none',
                                        animation: 'scanLaser 2s ease-in-out infinite',
                                        '@keyframes scanLaser': {
                                            '0%': { top: '10%' },
                                            '50%': { top: '88%' },
                                            '100%': { top: '10%' }
                                        }
                                    }}
                                />

                                {/* Face Target Oval Reticle */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: { xs: 170, sm: 210 },
                                        height: { xs: 220, sm: 260 },
                                        borderRadius: '50%',
                                        border: `2px dashed ${punchType === 'IN' ? '#34d399' : '#f87171'}`,
                                        boxShadow: `0 0 20px ${punchType === 'IN' ? 'rgba(52, 211, 153, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`,
                                        pointerEvents: 'none',
                                        animation: 'pulseOval 1.5s ease-in-out infinite alternate',
                                        '@keyframes pulseOval': {
                                            '0%': { transform: 'scale(0.98)', opacity: 0.8 },
                                            '100%': { transform: 'scale(1.02)', opacity: 1 }
                                        }
                                    }}
                                />

                                {/* Reticle HUD Corners */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 16,
                                        pointerEvents: 'none',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: 28,
                                            height: 28,
                                            borderTop: '3px solid #38bdf8',
                                            borderLeft: '3px solid #38bdf8'
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: 28,
                                            height: 28,
                                            borderTop: '3px solid #38bdf8',
                                            borderRight: '3px solid #38bdf8'
                                        }
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 16,
                                        pointerEvents: 'none',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: 28,
                                            height: 28,
                                            borderBottom: '3px solid #38bdf8',
                                            borderLeft: '3px solid #38bdf8'
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            width: 28,
                                            height: 28,
                                            borderBottom: '3px solid #38bdf8',
                                            borderRight: '3px solid #38bdf8'
                                        }
                                    }}
                                />

                                {/* Camera Toggle Controls */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        display: 'flex',
                                        gap: 1
                                    }}
                                >
                                    <Tooltip title="Switch Camera">
                                        <IconButton
                                            size="small"
                                            onClick={toggleCamera}
                                            sx={{
                                                bgcolor: 'rgba(15, 23, 42, 0.75)',
                                                color: '#e2e8f0',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                backdropFilter: 'blur(8px)',
                                                '&:hover': { bgcolor: 'rgba(14, 165, 233, 0.3)', color: '#38bdf8' }
                                            }}
                                        >
                                            <CameraswitchIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh Camera">
                                        <IconButton
                                            size="small"
                                            onClick={startCamera}
                                            sx={{
                                                bgcolor: 'rgba(15, 23, 42, 0.75)',
                                                color: '#e2e8f0',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                backdropFilter: 'blur(8px)',
                                                '&:hover': { bgcolor: 'rgba(14, 165, 233, 0.3)', color: '#38bdf8' }
                                            }}
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {/* Live Biometric Status Pill */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: 'rgba(15, 23, 42, 0.85)',
                                        px: 2,
                                        py: 0.6,
                                        borderRadius: 20,
                                        border: '1px solid rgba(255, 255, 255, 0.12)',
                                        backdropFilter: 'blur(8px)'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#10b981',
                                            boxShadow: '0 0 10px #10b981'
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '0.04em' }}>
                                        BIOMETRIC SCANNING ({scanProgress}%)
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Scan Progress & Instant Capture Button */}
                            <Box sx={{ width: '100%', mt: 2.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                                        Facial Alignment & Scan Analysis
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem', color: punchType === 'IN' ? '#059669' : '#dc2626', fontWeight: 700 }}>
                                        {scanProgress}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={scanProgress}
                                    sx={{
                                        height: 7,
                                        borderRadius: 3.5,
                                        bgcolor: '#e2e8f0',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 3.5,
                                            bgcolor: punchType === 'IN' ? '#10b981' : '#ef4444'
                                        }
                                    }}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<CameraAltIcon />}
                                    onClick={handleCaptureFrame}
                                    sx={{
                                        mt: 2,
                                        py: 1.2,
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        borderRadius: 2.5,
                                        textTransform: 'none',
                                        background: punchType === 'IN'
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        boxShadow: punchType === 'IN'
                                            ? '0 4px 14px rgba(16, 185, 129, 0.35)'
                                            : '0 4px 14px rgba(239, 68, 68, 0.35)',
                                        '&:hover': {
                                            background: punchType === 'IN'
                                                ? 'linear-gradient(135deg, #34d399 0%, #059669 100%)'
                                                : 'linear-gradient(135deg, #f87171 0%, #b91c1c 100%)'
                                        }
                                    }}
                                >
                                    Capture Photo & Verify Location
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* VIEW 2: REVIEW CAPTURED IMAGE & REAL-TIME PLACE BEFORE SUBMISSION */}
                    {scanStage === 'review' && (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {/* Captured Snapshot & Verification Badge */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    mb: 2.5,
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <Avatar
                                    src={capturedSnapshot?.previewUrl}
                                    alt="Face Snapshot"
                                    sx={{
                                        width: 130,
                                        height: 130,
                                        border: `4px solid ${punchType === 'IN' ? '#10b981' : '#ef4444'}`,
                                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)'
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -4,
                                        right: -4,
                                        bgcolor: '#ffffff',
                                        borderRadius: '50%',
                                        p: 0.3,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 26, color: '#10b981' }} />
                                </Box>
                            </Box>

                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', mb: 0.5 }}>
                                Biometric Photo Captured
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mb: 2 }}>
                                Camera has been turned off. Location must be verified to enable punch.
                            </Typography>

                            {/* Location Error Warning if permission was denied or unavailable */}
                            {locationError && (
                                <Alert
                                    severity="error"
                                    icon={<LocationOffIcon />}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            startIcon={<RefreshIcon />}
                                            onClick={fetchCurrentLocation}
                                            disabled={isLocating}
                                            sx={{ textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {isLocating ? 'Retrying...' : 'Retry'}
                                        </Button>
                                    }
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        borderRadius: 2.5,
                                        border: '1px solid #fecaca',
                                        fontSize: '0.82rem',
                                        alignItems: 'center'
                                    }}
                                >
                                    {locationError}
                                </Alert>
                            )}

                            {/* Verification Data Card */}
                            <Box
                                sx={{
                                    width: '100%',
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 3,
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    mb: 2.5
                                }}
                            >
                                {/* Place / Address Display */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                            <PlaceIcon sx={{ fontSize: 18, color: locationData ? '#059669' : '#dc2626' }} /> Real-time Location:
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                            {locationData ? `${locationData.latitude}, ${locationData.longitude}` : ''}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: locationData ? '#0f172a' : '#dc2626', pl: 3.2 }}>
                                        {isLocating ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0284c7' }}>
                                                <CircularProgress size={14} /> Acquiring Live GPS Location...
                                            </span>
                                        ) : locationData?.place ? (
                                            locationData.place
                                        ) : (
                                            'Location Not Provided (Required)'
                                        )}
                                    </Typography>
                                </Box>

                                <Divider sx={{ borderColor: '#f1f5f9' }} />

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                        <SensorsIcon sx={{ fontSize: 18, color: '#0284c7' }} /> Punch Action:
                                    </Typography>
                                    <Chip
                                        label={`PUNCH ${punchType}`}
                                        size="small"
                                        sx={{
                                            bgcolor: punchType === 'IN' ? '#ecfdf5' : '#fef2f2',
                                            color: punchType === 'IN' ? '#065f46' : '#991b1b',
                                            border: `1px solid ${punchType === 'IN' ? '#a7f3d0' : '#fecaca'}`,
                                            fontWeight: 800,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Action Buttons: Retake & Submit */}
                            <Box sx={{ width: '100%', display: 'flex', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ReplayIcon />}
                                    onClick={handleRetake}
                                    disabled={isSubmitting}
                                    sx={{
                                        flex: 1,
                                        py: 1.3,
                                        borderRadius: 2.5,
                                        borderColor: '#cbd5e1',
                                        color: '#475569',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        '&:hover': {
                                            borderColor: '#94a3b8',
                                            bgcolor: '#f8fafc'
                                        }
                                    }}
                                >
                                    Retake Photo
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : <SendIcon />}
                                    onClick={handleSubmitPunch}
                                    disabled={!isReadyToPunch || isSubmitting}
                                    sx={{
                                        flex: 2,
                                        py: 1.3,
                                        borderRadius: 2.5,
                                        fontWeight: 700,
                                        fontSize: '0.92rem',
                                        textTransform: 'none',
                                        background: punchType === 'IN'
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        boxShadow: punchType === 'IN'
                                            ? '0 6px 20px rgba(16, 185, 129, 0.4)'
                                            : '0 6px 20px rgba(239, 68, 68, 0.4)',
                                        '&:hover': {
                                            background: punchType === 'IN'
                                                ? 'linear-gradient(135deg, #34d399 0%, #059669 100%)'
                                                : 'linear-gradient(135deg, #f87171 0%, #b91c1c 100%)'
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: '#e2e8f0',
                                            color: '#94a3b8',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    {isSubmitting
                                        ? 'Recording Punch...'
                                        : isLocating
                                            ? 'Acquiring GPS...'
                                            : !locationData
                                                ? 'Location Required'
                                                : `Confirm & Submit Punch ${punchType}`}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* VIEW 3: CAMERA ERROR STATE */}
                    {scanStage === 'error' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 3, textAlign: 'center', maxWidth: 380 }}>
                            <VideocamOffIcon sx={{ fontSize: 52, color: '#dc2626' }} />
                            <Typography variant="subtitle1" sx={{ color: '#0f172a', fontWeight: 700 }}>
                                Camera Access Required
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {cameraError || 'Please allow webcam access in your browser to proceed with biometric verification.'}
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={startCamera}
                                sx={{
                                    mt: 1,
                                    color: '#0284c7',
                                    borderColor: '#bae6fd',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    '&:hover': {
                                        borderColor: '#0284c7',
                                        bgcolor: '#f0f9ff'
                                    }
                                }}
                            >
                                Retry Camera
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Biometric;